import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
// @mui
import { Collapse, List, Chip, Badge } from '@mui/material';
import { alpha } from '@mui/material/styles';
// hooks
import useAuth from '../../../hooks/useAuth';
import useCompany from '../../../hooks/useCompany';
import { useEmailFolders } from '../../../hooks/useEmail';
// config
import { FEATURE_FLAGS } from '../../../config';
// components
import Iconify from '../../Iconify';
import { ListItemStyle, ListItemTextStyle, ListItemIconStyle } from './style';
// routes
import { PATH_EMAIL } from '../../../routes/paths';

// 폴더 설정
const FOLDER_CONFIG = {
  INBOX: { icon: 'eva:inbox-fill', label: '받은편지함' },
  Sent: { icon: 'eva:paper-plane-fill', label: '보낸편지함' },
  Drafts: { icon: 'eva:file-text-fill', label: '임시보관함' },
  Trash: { icon: 'eva:trash-2-fill', label: '휴지통' },
};

export function EmailNavSection() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const router = useRouter();
  const { asPath, query } = router;
  const { data: folders } = useEmailFolders();

  const [isOpen, setIsOpen] = useState(false);

  const isEmailPage = asPath.startsWith('/email');

  // 이메일 페이지에 있으면 자동으로 펼침 (hooks는 조건문 전에 배치)
  useEffect(() => {
    if (isEmailPage) {
      setIsOpen(true);
    }
  }, [isEmailPage]);

  // 계약 중인 회사가 없으면 표시 안함
  if (!activeCompany || activeCompany.length === 0) return null;

  // Feature Flag 꺼져있으면 외부 웹메일 링크 표시
  if (!FEATURE_FLAGS.CREW_EMAIL_ENABLED) {
    const hasMigaduAccount = user?.has_migadu_account || false;
    if (!hasMigaduAccount) return null;

    return (
      <>
        <List disablePadding sx={{ px: 2 }}>
          <ListItemStyle
            component="a"
            href="https://webmail.migadu.com/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ cursor: 'pointer' }}
          >
            <ListItemIconStyle>
              <Iconify icon="eva:email-outline" width={20} height={20} />
            </ListItemIconStyle>
            <ListItemTextStyle disableTypography primary="DIO 메일" />
            <Iconify
              icon="eva:external-link-outline"
              width={16}
              height={16}
              sx={{ color: 'text.secondary' }}
            />
          </ListItemStyle>
        </List>
      </>
    );
  }

  const hasMigaduAccount = user?.has_migadu_account || false;
  const currentFolder = query.folder || 'INBOX';
  const totalUnread = folders?.find((f) => f.name === 'INBOX')?.unread || 0;

  const handleMainClick = () => {
    if (!isEmailPage) {
      router.push(PATH_EMAIL.root);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleFolderClick = (folderName) => {
    router.push({ pathname: PATH_EMAIL.root, query: { folder: folderName } }, undefined, { shallow: true });
  };

  const getFolderUnread = (folderName) => {
    return folders?.find((f) => f.name === folderName)?.unread || 0;
  };

  return (
    <>
      <List disablePadding sx={{ px: 2, pt: 2 }}>
        {/* 메인 메뉴: 디오메일 */}
        <ListItemStyle
          onClick={handleMainClick}
          activeRoot={isEmailPage}
          sx={{ cursor: 'pointer' }}
        >
        <ListItemIconStyle>
          <Iconify
            icon={isEmailPage ? 'eva:email-fill' : 'eva:email-outline'}
            width={20}
            height={20}
          />
        </ListItemIconStyle>
        <ListItemTextStyle disableTypography primary="DIO 메일" />
        {hasMigaduAccount && totalUnread > 0 && (
          <Chip
            label={totalUnread > 99 ? '99+' : totalUnread}
            size="small"
            sx={{
              height: 20,
              minWidth: 20,
              fontSize: '0.7rem',
              color: 'white',
              bgcolor: 'error.main',
              fontWeight: 'bold',
              mr: 1,
            }}
          />
        )}
        {hasMigaduAccount && (
          <Iconify
            icon={isOpen ? 'eva:chevron-down-fill' : 'eva:chevron-right-fill'}
            width={16}
            height={16}
            sx={{ color: 'text.secondary' }}
          />
        )}
      </ListItemStyle>

      {/* 서브메뉴: 폴더 목록 */}
      {hasMigaduAccount && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding sx={{ pl: 1 }}>
            {Object.entries(FOLDER_CONFIG).map(([folderName, config]) => {
              const isSelected = isEmailPage && currentFolder === folderName;
              const unread = getFolderUnread(folderName);

              return (
                <ListItemStyle
                  key={folderName}
                  onClick={() => handleFolderClick(folderName)}
                  subItem
                  activeSub={isSelected}
                  sx={{
                    cursor: 'pointer',
                    pl: 2,
                    ...(isSelected && {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    }),
                  }}
                >
                  <ListItemIconStyle sx={{ mr: 1.5, minWidth: 'auto' }}>
                    <Iconify
                      icon={config.icon}
                      width={18}
                      height={18}
                      sx={{ color: isSelected ? 'primary.main' : 'text.secondary' }}
                    />
                  </ListItemIconStyle>
                  <ListItemTextStyle
                    disableTypography
                    primary={config.label}
                    sx={{
                      fontSize: '0.8125rem',
                      color: isSelected ? 'primary.main' : 'text.secondary',
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  />
                  {unread > 0 && (
                    <Badge
                      badgeContent={unread > 99 ? '99+' : unread}
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.6rem',
                          height: 16,
                          minWidth: 16,
                        },
                      }}
                    />
                  )}
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      )}
      </List>
    </>
  );
}
